import {TUTORIAL_KEY_MODAL} from "modules";
import {closeTutorial, setActiveStep, setIsOpenMobileNavigation} from "modules/actions";
import {getIsTutorialOpen, getTutorialStep, getViewedEvent} from "modules/selectors";
import React, {Fragment, useCallback, useState} from "react";
import Joyride, {ACTIONS, CallBackProps, EVENTS, STATUS, Step} from "react-joyride";
import {useDispatch, useSelector} from "react-redux";
import {useMedia} from "react-use";
import {BOOSTER_STEP, TooltipContent} from "./TooltipContent";
import {TourTooltip} from "./TourTooltip";

const cartTarget = ".tutorial-cart";
const NAVIGATION_STEP_INDEX = 7;

export const Tutorial: React.FC = () => {
	const dispatch = useDispatch();
	const [isHideTutorial, setIsHideTutorial] = useState(false);
	const isTutorialOpen = useSelector(getIsTutorialOpen);
	const isOpen = isTutorialOpen && !isHideTutorial;
	const step = useSelector(getTutorialStep);
	const nextEvent = useSelector(getViewedEvent);
	const isMobile = useMedia("(max-width: 768px)");

	const steps: Step[] = [
		{
			target: "body",
			content: (
				<TooltipContent
					title="Welcome!"
					message="To get started, you must select six golfers, four in your starting line-up and two on the bench. You will earn points based on how they perform in the upcoming tournament."
				/>
			),
			placement: "center",
			disableBeacon: true,
			floaterProps: {
				hideArrow: true,
			},
		},
		{
			target: ".starters",
			content: (
				<TooltipContent
					title="Build your Team"
					message="Pick any four golfers who are in this week’s field to take part in your starting line-up. Designate one as your captain - his points will be doubled."
				/>
			),
			floaterProps: {
				placement: "bottom",
			},
			placement: "bottom",
		},
		{
			target: ".bench",
			content: (
				<TooltipContent
					title="Select Your Bench"
					message="Pick any two golfers who are in this week’s field to sit on your bench. You can make changes to your line-up between each round."
				/>
			),
			floaterProps: {
				placement: "bottom",
			},
			placement: "bottom",
		},
		BOOSTER_STEP,
		{
			target: cartTarget,
			content: (
				<TooltipContent
					title="Earn Points"
					message="Golfers will score points based on a number of statistics, including birdies, pars, low rounds as well as their finishing position in the tournament."
				/>
			),
			floaterProps: {
				placement: "bottom",
			},
			placement: "bottom",
		},
		{
			target: ".captain-icon",
			content: (
				<TooltipContent
					title="Selection Cap"
					message="
					You must pick a fresh team each week. But be careful…<br/>
					<ul>
						<li>DP World Tour members can be selected a maximum of <b>five</b> times in the <b>season</b></li>
						<li>Non-DP World Tour members can be selected a maximum of <b>three</b> times in the <b>season</b></li>
					</ul>
					Once you have reached the maximum selections for a player, they will be greyed out in the Golfer Pool and be unavailable for selection, so choose strategically and wisely!"
				/>
			),
			spotlightPadding: 4,
			placement: "bottom",
		},
		{
			target: ".team-players",
			content: (
				<TooltipContent
					title="Forget To Pick?"
					message="If you forget to pick a team, you will automatically be assigned six random players (including a captain) at the start of a tournament, to ensure you don’t miss out on points."
				/>
			),
			placement: "bottom",
		},
		{
			target: isMobile ? '.mobile-nav a[href="/leagues"]' : 'a[href="/leagues"]',
			content: (
				<TooltipContent
					title="Join/Create Leagues"
					message="Challenge your friends by creating and joining different leagues!"
				/>
			),
			placement: "bottom",
		},
		{
			target: "body",
			content: (
				<TooltipContent
					title="You’re Ready!"
					message={`What are you waiting for? Pick your team ${
						nextEvent ? "for the " + nextEvent.name : ""
					} now!`}
				/>
			),
			placement: "center",
		},
	];

	const nextStepHandler = useCallback(
		(action: string, index: number) => {
			const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
			dispatch(setActiveStep(nextIndex));

			dispatch(setIsOpenMobileNavigation(nextIndex === NAVIGATION_STEP_INDEX && isMobile));

			// step with navigation
			if (nextIndex === NAVIGATION_STEP_INDEX && isMobile) {
				setIsHideTutorial(true);

				setTimeout(() => {
					// wait for the mobile navigation to open
					setIsHideTutorial(false);
				}, 700);
			}
		},
		[dispatch, isMobile]
	);

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const {action, index, status, type} = data;

			if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
				nextStepHandler(action, index);
			} else if (
				status === STATUS.FINISHED ||
				status === STATUS.SKIPPED ||
				action === "close" ||
				action === "stop"
			) {
				localStorage.setItem(TUTORIAL_KEY_MODAL, "true");
				dispatch(closeTutorial());
			}
		},
		[dispatch, nextStepHandler]
	);

	return (
		<Fragment>
			<Joyride
				stepIndex={step}
				scrollToFirstStep
				callback={handleJoyrideCallback}
				steps={steps}
				run={isOpen}
				tooltipComponent={TourTooltip}
				continuous={true}
			/>
		</Fragment>
	);
};
